import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, CheckCircle2, FolderOpen, GraduationCap, LogOut, Star, Wallet } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field, KpiCard } from "@/components/common/Page";
import { EmptyState, ErrorState, LoadingState, Spinner } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import AuthShell from "@/pages/auth/AuthShell";
import { MaterialList } from "@/components/students/MaterialsPanel";
import { brl, fmtDate, fmtShort, KINDS, weekdayName } from "@/lib/format";

const List = ({ children, empty }) => (children.length ? <div className="divide-y rounded-xl border bg-card">{children}</div> : <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>);

const PortalMaterials = () => {
  const { data, error, reload } = useFetch("/portal/materials");
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <LoadingState rows={2} />;
  return data.length ? <MaterialList items={data} />
    : <EmptyState icon={FolderOpen} title="Nenhum material disponível" description="Quando seu professor enviar apostilas ou exercícios, eles aparecerão aqui." testId="portal-materials-empty" />;
};

export function PortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data, error, reload } = useFetch("/portal/overview");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/80 px-4 backdrop-blur-md sm:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><GraduationCap className="h-5 w-5" /></div>
          <span className="font-heading text-lg font-bold">Portal do aluno</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/login"); }} data-testid="portal-logout-btn"><LogOut className="mr-2 h-4 w-4" />Sair</Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
        {error ? <ErrorState error={error} onRetry={reload} /> : !data ? <LoadingState rows={4} /> : (
          <div className="animate-in fade-in duration-300">
            <p className="text-sm text-muted-foreground">Olá, {user.first_name}{user.relation === "responsavel" ? " · Responsável" : ""}</p>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight" data-testid="portal-student-name">{data.student.name}</h1>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="portal-teacher-info">Professor(a): {data.teacher.name}{data.teacher.phone && ` · ${data.teacher.phone}`}</p>
            <div className="my-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KpiCard label="Próximas aulas" value={data.upcoming.length} icon={CalendarDays} testId="portal-kpi-upcoming" />
              <KpiCard label="Presença" value={data.stats.presenca_pct == null ? "—" : `${data.stats.presenca_pct}%`} icon={CheckCircle2} tone="text-accent" testId="portal-kpi-attendance" />
              <KpiCard label="Média das notas" value={data.stats.media ?? "—"} icon={Star} testId="portal-kpi-average" />
              <KpiCard label="Em aberto" value={brl(data.stats.pendente)} icon={Wallet} tone="text-amber-500" testId="portal-kpi-pending" />
            </div>
            {data.packages.map((p) => (
              <div key={p.id} className="mb-4 rounded-xl border bg-card p-4 text-sm" data-testid={`portal-package-${p.id}`}>
                <span className="font-semibold">{p.name}:</span> {p.used} de {p.total_classes} aulas realizadas · <span className="text-primary">{p.remaining} restantes</span>
              </div>
            ))}
            <Tabs defaultValue="proximas">
              <TabsList className="mb-4 h-auto w-full justify-start overflow-x-auto">
                {[["proximas", "Próximas aulas"], ["historico", "Histórico e presença"], ["desempenho", "Desempenho"], ["pagamentos", "Pagamentos"], ["materiais", "Materiais"]].map(([k, l]) => (
                  <TabsTrigger key={k} value={k} data-testid={`portal-tab-${k}`}>{l}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="proximas">
                <List empty="Nenhuma aula agendada.">
                  {data.upcoming.map((c) => (
                    <div key={c.id} className="flex items-center gap-4 px-4 py-3 text-sm" data-testid={`portal-upcoming-${c.id}`}>
                      <div className="w-28"><p className="font-medium">{weekdayName(c.date)}</p><p className="font-mono text-xs text-muted-foreground">{fmtDate(c.date)}</p></div>
                      <span className="w-12 font-mono text-primary">{c.start_time}</span>
                      <span className="flex-1">{c.subject} · {c.duration} min · {c.type === "online" ? "Online" : "Presencial"}</span>
                      <StatusBadge status={c.status} />
                    </div>
                  ))}
                </List>
              </TabsContent>
              <TabsContent value="historico">
                <List empty="Nenhuma aula no histórico.">
                  {data.history.map((c) => (
                    <div key={c.id} className="px-4 py-3 text-sm" data-testid={`portal-history-${c.id}`}>
                      <div className="flex items-center gap-4">
                        <span className="w-24 font-mono">{fmtDate(c.date)}</span><span className="flex-1">{c.subject}</span><StatusBadge status={c.status} />
                      </div>
                      {c.note && (c.note.content || c.note.homework) && (
                        <div className="mt-2 space-y-0.5 rounded-lg bg-muted/50 p-3 text-xs">
                          {c.note.content && <p><span className="font-semibold">Conteúdo: </span>{c.note.content}</p>}
                          {c.note.homework && <p><span className="font-semibold">Tarefa de casa: </span>{c.note.homework}</p>}
                          {c.note.next_content && <p><span className="font-semibold">Próxima aula: </span>{c.note.next_content}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </List>
              </TabsContent>
              <TabsContent value="desempenho">
                {data.assessments.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Nenhuma avaliação registrada.</p> : (
                  <div className="space-y-4">
                    <div className="h-56 rounded-xl border bg-card p-4" data-testid="portal-assessment-chart">
                      <ResponsiveContainer>
                        <LineChart data={data.assessments.map((a) => ({ ...a, label: fmtShort(a.date) }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                          <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} /><YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={11} width={30} />
                          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                          <Line type="monotone" dataKey="score" name="Nota" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <List empty="">
                      {[...data.assessments].reverse().map((a) => (
                        <div key={a.id} className="flex items-center gap-4 px-4 py-3 text-sm"><span className="w-24 font-mono">{fmtDate(a.date)}</span><span className="flex-1">{a.subject}{a.content && ` — ${a.content}`}</span><span className="font-mono text-lg font-semibold text-primary">{a.score.toFixed(1)}</span></div>
                      ))}
                    </List>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="pagamentos">
                <List empty="Nenhum pagamento registrado.">
                  {data.payments.map((p) => (
                    <div key={p.id} className="flex items-center gap-4 px-4 py-3 text-sm" data-testid={`portal-payment-${p.id}`}>
                      <span className="w-24 font-mono">{fmtDate(p.due_date)}</span>
                      <span className="flex-1">{p.description || KINDS[p.kind]}{p.paid_date && <span className="block text-xs text-muted-foreground">Pago em {fmtDate(p.paid_date)}</span>}</span>
                      <span className="font-mono font-semibold">{brl(p.amount)}</span><StatusBadge status={p.status} />
                    </div>
                  ))}
                </List>
              </TabsContent>
              <TabsContent value="materiais"><PortalMaterials /></TabsContent>
            </Tabs>
          </div>
        )}
      </main>
    </div>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const info = useFetch(`/portal/invite/${encodeURIComponent(token)}`);
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/portal/accept", { ...form, token });
      setUser(data);
      toast.success("Acesso criado! Bem-vindo ao portal.");
      navigate("/portal");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Acesso ao portal do aluno" subtitle={info.data ? `${info.data.teacher_name} convidou você para acompanhar ${info.data.student_name}.` : undefined}>
      {info.loading ? <LoadingState rows={2} /> : info.error ? (
        <div className="space-y-4" data-testid="invite-invalid"><ErrorState error={info.error} /><Button asChild variant="outline"><Link to="/login">Ir para o login</Link></Button></div>
      ) : (
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="accept-invite-form">
          <Field label="E-mail" className="sm:col-span-2"><Input value={info.data.email} disabled data-testid="invite-email" /></Field>
          <Field label="Nome"><Input value={form.first_name} onChange={set("first_name")} required data-testid="invite-first-name-input" /></Field>
          <Field label="Sobrenome"><Input value={form.last_name} onChange={set("last_name")} required data-testid="invite-last-name-input" /></Field>
          <Field label="Senha" hint="Mínimo 8 caracteres, com letras e números"><Input type="password" value={form.password} onChange={set("password")} required data-testid="invite-password-input" /></Field>
          <Field label="Confirmar senha"><Input type="password" value={form.confirm_password} onChange={set("confirm_password")} required data-testid="invite-confirm-input" /></Field>
          {error && <p className="text-sm text-destructive sm:col-span-2" data-testid="invite-error">{error}</p>}
          <Button type="submit" className="sm:col-span-2" disabled={busy} data-testid="invite-submit-btn">{busy && <Spinner className="mr-2" />}Criar acesso</Button>
        </form>
      )}
    </AuthShell>
  );
}
